import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { KanbanColumn } from './KanbanColumn';

export interface KanbanColumnConfig<TStatus extends string> {
  status: TStatus;
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'purple' | 'cyan' | 'orange' | 'zinc';
}

export interface KanbanBoardProps<TItem, TStatus extends string> {
  items: TItem[];
  columns: KanbanColumnConfig<TStatus>[];
  getItemStatus: (item: TItem) => TStatus;
  getItemId: (item: TItem) => string;
  renderCard: (item: TItem, isDragging?: boolean) => React.ReactNode;
  onStatusChange: (itemId: string, newStatus: TStatus) => Promise<void>;
  isLoading?: boolean;
  /**
   * Fill the parent instead of sizing to content: columns share the width
   * (no sideways scroll) and stretch to the parent's height. The parent
   * must give it a height — see SidebarLayout's `fill`.
   */
  fill?: boolean;
  /**
   * Show the board without letting anything move. For a viewer the server
   * would refuse the status change anyway (a BoardMember, #116): a card
   * that drags and then snaps back on a 403 is a control that looks live
   * and fails. Cards stay clickable.
   */
  readOnly?: boolean;
}

export function KanbanBoard<TItem, TStatus extends string>({
  items,
  columns,
  getItemStatus,
  getItemId,
  renderCard,
  onStatusChange,
  isLoading = false,
  fill = false,
  readOnly = false,
}: KanbanBoardProps<TItem, TStatus>): React.ReactElement {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [optimisticItems, setOptimisticItems] = useState<Map<string, TStatus>>(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a set of valid column statuses for quick lookup
  const validStatuses = useMemo(() => {
    return new Set(columns.map((col) => col.status));
  }, [columns]);

  // Group items by status, applying optimistic updates
  const itemsByStatus = useMemo(() => {
    const grouped = new Map<TStatus, TItem[]>();

    // Initialize all columns with empty arrays
    columns.forEach((col) => {
      grouped.set(col.status, []);
    });

    // Group items, using optimistic status if available
    // Items with unrecognized statuses go to the first column
    const firstColumnStatus = columns[0]?.status;

    items.forEach((item) => {
      const itemId = getItemId(item);
      const rawStatus = optimisticItems.get(itemId) ?? getItemStatus(item);

      // Use the status if valid, otherwise fall back to first column
      const status = validStatuses.has(rawStatus) ? rawStatus : firstColumnStatus;

      if (status) {
        // Log unmatched statuses in development to help debugging
        if (rawStatus !== status && process.env.NODE_ENV === 'development') {
          console.warn(`Kanban: Item "${itemId}" has unrecognized status "${rawStatus}", placing in "${status}" column`);
        }

        const current = grouped.get(status) || [];
        grouped.set(status, [...current, item]);
      }
    });

    return grouped;
  }, [items, columns, getItemStatus, getItemId, optimisticItems, validStatuses]);

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return items.find((item) => getItemId(item) === activeId) || null;
  }, [activeId, items, getItemId]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeItemId = active.id as string;
    const overId = over.id as string;

    // Find which column we're over (either the column itself or an item in it)
    let targetStatus: TStatus | null = null;

    // Check if we're over a column directly
    const overColumn = columns.find((col) => col.status === overId);
    if (overColumn) {
      targetStatus = overColumn.status;
    } else {
      // We're over an item, find its column
      const overItem = items.find((item) => getItemId(item) === overId);
      if (overItem) {
        targetStatus = optimisticItems.get(overId) ?? getItemStatus(overItem);
      }
    }

    if (targetStatus !== null) {
      const validTargetStatus = targetStatus as TStatus;
      const currentStatus = optimisticItems.get(activeItemId) ??
        getItemStatus(items.find((item) => getItemId(item) === activeItemId)!);

      if (currentStatus !== validTargetStatus) {
        setOptimisticItems((prev) => new Map(prev).set(activeItemId, validTargetStatus));
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) {
      // Drag cancelled, reset optimistic state
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(active.id as string);
        return next;
      });
      return;
    }

    const activeItemId = active.id as string;
    const overId = over.id as string;

    // Determine target status
    let targetStatus: TStatus | null = null;
    const overColumn = columns.find((col) => col.status === overId);
    if (overColumn) {
      targetStatus = overColumn.status;
    } else {
      const overItem = items.find((item) => getItemId(item) === overId);
      if (overItem) {
        targetStatus = optimisticItems.get(overId) ?? getItemStatus(overItem);
      }
    }

    const activeItem = items.find((item) => getItemId(item) === activeItemId);
    if (!activeItem || !targetStatus) {
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(activeItemId);
        return next;
      });
      return;
    }

    const currentStatus = getItemStatus(activeItem);
    if (currentStatus === targetStatus) {
      // No status change needed
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(activeItemId);
        return next;
      });
      return;
    }

    try {
      await onStatusChange(activeItemId, targetStatus);
      // Clear optimistic state after successful update
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(activeItemId);
        return next;
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      // Revert optimistic update on error
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(activeItemId);
        return next;
      });
    }
  };

  const handleDragCancel = () => {
    if (activeId) {
      setOptimisticItems((prev) => {
        const next = new Map(prev);
        next.delete(activeId);
        return next;
      });
    }
    setActiveId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        className={
          fill ? 'flex h-full min-h-0 gap-3' : 'flex gap-4 overflow-x-auto pb-4 min-h-[500px]'
        }
      >
        {columns.map((column) => (
          <KanbanColumn
            key={column.status}
            id={column.status}
            title={column.label}
            color={column.color}
            items={itemsByStatus.get(column.status) || []}
            getItemId={getItemId}
            renderCard={renderCard}
            fill={fill}
            readOnly={readOnly}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="rotate-3 opacity-90">
            {renderCard(activeItem, true)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default KanbanBoard;
